import find from "lodash/find";
import omit from "lodash/omit";
import { SavedFiltersProps, useFiltersV2Select, useFiltersV2Store } from "PFApp/components/filters_v2";
import { generateApiFilters } from "PFApp/components/filters_v2/helpers/generate_api_filters";
import { Modal } from "PFComponents/modal";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useSavedFiltersCreate } from "PFCore/hooks/queries/filters";
import { useSavedFiltersV2 } from "PFCore/hooks/queries/filters/use_saved_filters_v2";
import { SavedFilterCreateData } from "PFCore/services/filters";
import { FiltersV2 } from "PFCore/types/filters_v2";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type SavedFiltersCollectionCreateModalProps = {
  onClose: VoidFunction;
} & SavedFiltersProps;

export const SavedFiltersCollectionCreateModal = ({
  onClose,
  omit: excludedFilters
}: SavedFiltersCollectionCreateModalProps) => {
  const { t } = useTranslation("filters", { keyPrefix: "save" });

  const [viewName, setViewName] = useState<string>("");
  const { collection } = useFiltersV2Select("collection");
  const { mutateAsync: createSavedFilter } = useSavedFiltersCreate(collection!);

  const { data: savedCollections } = useSavedFiltersV2(collection!);

  const isViewNameUsed = !!find(savedCollections, { name: viewName });

  const areChildrenOn = useFiltersV2Store((state) => state.buffer.areChildrenOn);
  const mainFilters = useFiltersV2Store(
    (state) => omit(state.buffer.mainFilters, excludedFilters?.main || []) as FiltersV2
  );
  const childrenFilters = useFiltersV2Store(
    (state) => omit(state.buffer.childrenFilters, excludedFilters?.children || []) as FiltersV2
  );

  const handleSave = () => {
    const newSavedFilter: SavedFilterCreateData = {
      name: viewName,
      target: collection!,
      ...generateApiFilters(
        {
          mainFilters,
          childrenFilters,
          areChildrenOn
        },
        { sendDefaultsAsUndefined: false, sendEmptyAsUndefined: false }
      )
    };

    createSavedFilter(newSavedFilter);
    setViewName("");
  };

  const isAcceptEnabled = viewName && !isViewNameUsed && viewName.trim();

  const modalTitle = t("filterNameModalTitle");

  return (
    <Modal
      title={modalTitle}
      disableOKButton={!isAcceptEnabled}
      onOK={handleSave}
      onClose={() => {
        setViewName("");
        onClose();
      }}
    >
      <InputFieldSet
        label={t("filterName")}
        value={viewName ?? ""}
        maxLength={100}
        required
        error={isViewNameUsed ? t("nameUsed") : ""}
        onChange={setViewName}
      />
    </Modal>
  );
};
